import React, { useState } from 'react';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link,
  Divider,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { useAuth } from '../contexts/AuthContext';
import { ErrorReportDialog } from './ErrorReportDialog';

const USER_ERRORS = new Set([
  'auth/invalid-email', 'auth/user-disabled', 'auth/user-not-found',
  'auth/wrong-password', 'auth/too-many-requests', 'auth/invalid-credential',
  'auth/popup-closed-by-user', 'auth/cancelled-popup-request', 'auth/popup-blocked',
]);

export const Login: React.FC = () => {
  // See SignUp.tsx — `?invite=` prefills an invited address; the brewery is
  // joined by server-side email matching, not by this parameter.
  const [searchParams] = useSearchParams();
  const invitedEmail = searchParams.get('invite') || '';
  const [email, setEmail] = useState(invitedEmail);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [reportError, setReportError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const { signIn, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError(null);
      setLoading(true);
      await signIn(email, password);
      navigate('/settings');
    } catch (error: any) {
      console.error('Error signing in:', error);
      let errorMessage = 'Failed to sign in. Please check your credentials.';
      if (error?.code) {
        switch (error.code) {
          case 'auth/invalid-email': errorMessage = 'Invalid email address.'; break;
          case 'auth/user-disabled': errorMessage = 'This account has been disabled.'; break;
          case 'auth/user-not-found':
          case 'auth/wrong-password':
          case 'auth/invalid-credential': errorMessage = 'Invalid email or password.'; break;
          case 'auth/too-many-requests': errorMessage = 'Too many failed login attempts. Please try again later.'; break;
          default: errorMessage = error.message || errorMessage;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      if (!USER_ERRORS.has(error?.code)) setReportError(error);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setError(null);
      setLoading(true);
      await signInWithGoogle();
      navigate('/settings');
    } catch (error: any) {
      console.error('Error signing in with Google:', error);
      setLoading(false);
      if (error?.code === 'auth/cancelled-popup-request') return;
      let errorMessage = 'Failed to sign in with Google.';
      if (error?.code) {
        switch (error.code) {
          case 'auth/popup-closed-by-user': errorMessage = 'Sign-in popup was closed. Please try again.'; break;
          case 'auth/popup-blocked': errorMessage = 'Sign-in popup was blocked. Please allow popups for this site.'; break;
          case 'auth/operation-not-allowed': errorMessage = 'Google sign-in is not enabled. Please contact support.'; break;
          default: errorMessage = error.message || errorMessage;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      if (!USER_ERRORS.has(error?.code)) setReportError(error);
      setError(errorMessage);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 2 }}>
      <ErrorReportDialog
        open={reportError !== null}
        error={reportError}
        onClose={() => setReportError(null)}
      />
      <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%' }}>
        <Typography variant="h5" component="h1" gutterBottom align="center">
          Sign In
        </Typography>

        {invitedEmail && (
          <Alert severity="info" sx={{ mt: 2 }}>
            You've been invited to join a brewery on Batchfather. Sign in as{' '}
            <strong>{invitedEmail}</strong> to accept — you'll be added automatically.
          </Alert>
        )}

        {/* Google Sign-In Button */}
        <Button
          fullWidth
          variant="outlined"
          startIcon={<GoogleIcon />}
          onClick={handleGoogleSignIn}
          disabled={loading}
          sx={{ mt: 2 }}
        >
          Continue with Google
        </Button>

        <Divider sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary">
            OR
          </Typography>
        </Divider>

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            required
            autoComplete="email"
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
            autoComplete="current-password"
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}
              action={reportError ? (
                <Button color="inherit" size="small" onClick={() => setReportError(reportError)}>
                  Report
                </Button>
              ) : undefined}
            >
              {error}
            </Alert>
          )}
          <Button
            fullWidth
            variant="contained"
            color="primary"
            type="submit"
            disabled={loading}
            sx={{ mt: 3 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Sign In'}
          </Button>
        </form>

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Link component={RouterLink} to="/forgot-password" variant="body2" sx={{ display: 'block', mb: 1 }}>
            Forgot Password?
          </Link>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ mb: 1 }}>
            Don't have an account?
          </Typography>
          <Button
            component={RouterLink}
            to={invitedEmail ? `/signup?invite=${encodeURIComponent(invitedEmail)}` : '/signup'}
            variant="outlined"
            fullWidth
          >
            Create Account
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}; 